import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Lock, CreditCard, LogOut, Crown, Mail, Check, Info, X, ChevronDown } from 'lucide-react';
import PricingCards from '../components/payment/PricingCards';
import AboutContent from '../components/settings/AboutContent'; 
import { useAuth } from '../context/AuthContext';
import { ROUTES, PRICING } from '../lib/constants';
import toast from 'react-hot-toast';

const Settings = () => {
  const navigate = useNavigate();
  const { user, profile, signOut, updateProfile, isPremium } = useAuth();

  const [activeTab, setActiveTab] = useState('profile');
  const [fullName, setFullName] = useState(profile?.full_name || '');
  const [saving, setSaving] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  
  const [mobileSections, setMobileSections] = useState({
    profile: true,
    account: false,
    subscription: false,
    about: false,
  });

  useEffect(() => {
    if (profile?.full_name) setFullName(profile.full_name);
  }, [profile]);

  const toggleMobileSection = (section) => {
    setMobileSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleSaveProfile = async () => {
    if (!fullName.trim()) return toast.error('Name cannot be empty');
    setSaving(true);
    try {
      await updateProfile({ full_name: fullName });
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate(ROUTES.LOGIN);
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile Information', icon: User },
    { id: 'account', label: 'Account Security', icon: Lock },
    { id: 'subscription', label: 'Subscription Plan', icon: CreditCard },
    { id: 'about', label: 'About & Help Info', icon: Info },
  ];

  const renderTabContent = (tabId) => {
    switch (tabId) {
      case 'profile':
        return <ProfileContent fullName={fullName} setFullName={setFullName} user={user} profile={profile} saving={saving} onSave={handleSaveProfile} />;
      case 'account':
        return <AccountContent onNavigate={() => navigate(ROUTES.FORGOT_PASSWORD)} />;
      case 'subscription':
        return <SubscriptionContent isPremium={isPremium} profile={profile} onUpgrade={() => setShowPricingModal(true)} />;
      case 'about':
        return <AboutContent onNavigate={(route) => navigate(route)} profile={profile} />;
      default:
        return null;
    }
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-black text-brown-900 tracking-tight">Settings</h1>
        <p className="text-xs sm:text-sm text-brown-600 mt-0.5">Modify profile, about us, and premium settings</p>
      </div>

      {/* 📱 MOBILE LAYOUT ACCORDION */}
      <div className="block lg:hidden space-y-3">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isOpen = mobileSections[tab.id];
          return (
            <div key={tab.id} className="bg-white/60 backdrop-blur-sm border border-cream-300 rounded-2xl overflow-hidden shadow-sm">
              <button 
                onClick={() => toggleMobileSection(tab.id)}
                className="w-full flex items-center justify-between p-4 bg-white/40 active:bg-cream-100/40"
              >
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-xl bg-brown-50 text-brown-600"><Icon className="w-5 h-5" /></div>
                  <span className="font-bold text-sm sm:text-base text-brown-900">{tab.label}</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-brown-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              {isOpen && (
                <div className="p-4 border-t border-cream-200/60 bg-white/20 animate-fade-in">
                  {renderTabContent(tab.id)}
                </div>
              )}
            </div>
          );
        })}

        <button onClick={handleLogout} className="w-full flex items-center justify-between p-4 bg-red-50/60 border border-red-200/60 rounded-2xl font-bold text-red-600 shadow-sm">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-xl bg-red-100 text-red-600"><LogOut className="w-5 h-5" /></div>
            <span className="text-sm sm:text-base">Log Out</span>
          </div>
        </button>
      </div>

      {/* 🖥️ DESKTOP LAYOUT */}
      <div className="hidden lg:grid grid-cols-4 gap-6">
        <div className="col-span-1">
          <div className="backdrop-blur-sm bg-white/40 rounded-2xl border border-cream-300/50 p-3 shadow-sm">
            <nav className="space-y-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-3 w-full px-4 py-3 rounded-xl transition-all ${
                      isActive ? 'bg-gradient-to-r from-brown-600 to-brown-700 text-white shadow-md font-semibold' : 'text-brown-700 hover:bg-white/60'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-brown-500'}`} />
                    <span className="text-sm font-medium">{tab.label.split(' ')[0]}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        <div className="col-span-3">
          <div className="backdrop-blur-sm bg-white/40 rounded-2xl border border-cream-300/50 p-8 shadow-sm">
            {renderTabContent(activeTab)}
          </div>
        </div>
      </div>

      {showPricingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="relative max-w-6xl w-full max-h-[90vh] overflow-y-auto bg-gradient-to-br from-cream-50 to-brown-50 rounded-2xl shadow-2xl p-6">
            <button onClick={() => setShowPricingModal(false)} className="absolute top-4 right-4 p-2 hover:bg-brown-100 rounded-lg">
              <X className="w-6 h-6 text-brown-700" />
            </button>
            <PricingCards onSuccess={() => { setShowPricingModal(false); toast.success('Welcome to Premium! 🎉'); window.location.reload(); }} />
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// 🧩 SUB-COMPONENTS FOR INTERACTIVE FIELDS
// ==========================================

const ProfileContent = ({ fullName, setFullName, user, profile, saving, onSave }) => (
  <div className="space-y-4 sm:space-y-6">
    <div>
      <h3 className="hidden lg:block text-lg font-bold text-brown-900 mb-2">Profile Information</h3>
      <p className="text-xs text-brown-600">Update your personal details.</p>
    </div>
    <div>
      <label className="block text-sm font-medium text-brown-800 mb-2">Full Name</label>
      <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} className="w-full px-4 py-2 bg-white border-2 border-cream-300 rounded-xl outline-none text-sm" />
    </div>
    <div>
      <label className="block text-sm font-medium text-brown-800 mb-2">Email Address</label>
      <div className="relative">
        <input type="email" value={user?.email || ''} disabled className="w-full px-4 py-2 bg-cream-100/50 border-2 border-cream-300 rounded-xl text-brown-600 text-sm cursor-not-allowed" />
        <Mail className="absolute right-4 top-1/2 -translate-y-1/2 text-brown-400 w-4 h-4" />
      </div>
    </div>
    <button onClick={onSave} disabled={saving || fullName === profile?.full_name} className="flex items-center space-x-2 px-5 py-2.5 bg-gradient-to-r from-brown-600 to-brown-700 text-white rounded-xl font-medium disabled:opacity-50 text-sm">
      {saving ? <span>Saving...</span> : <><Check className="w-4 h-4" /><span>Save Changes</span></>}
    </button>
  </div>
);

const AccountContent = ({ onNavigate }) => (
  <div className="space-y-4 sm:space-y-6">
    <div>
      <h3 className="hidden lg:block text-lg font-bold text-brown-900 mb-2">Account Settings</h3>
      <p className="text-xs text-brown-600">Manage password and delete account options</p>
    </div>
    <div className="border border-cream-300 rounded-xl p-4 bg-white/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div className="flex-1">
        <h4 className="text-sm font-semibold text-brown-900">Password</h4>
        <p className="text-xs text-brown-600">Update your account password</p>
      </div>
      <button onClick={onNavigate} className="px-4 py-2 border-2 border-brown-600 text-brown-700 rounded-xl text-xs sm:text-sm">Change Password</button>
    </div>
    <div className="border border-red-200 rounded-xl p-4 bg-red-50/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div className="flex-1">
        <h4 className="text-sm font-semibold text-red-900">Delete Account</h4>
        <p className="text-xs text-red-700">Permanently wipe historical documents and your data</p>
      </div>
      <button onClick={() => alert('Account deletion operations can be initiated via direct email contact')} className="px-4 py-2 border-2 border-red-500 text-red-700 rounded-xl text-xs sm:text-sm">Delete Account</button>
    </div>
  </div>
);

const SubscriptionContent = ({ isPremium, profile, onUpgrade }) => {
  const getDisplayPeriod = (plan) => {
    const periods = { 'weekly': 'week', 'monthly': 'month', 'semester': 'semester' };
    return periods[plan?.toLowerCase()] || 'month';
  };
  
  // ✅ FIXED: Safely resolve dynamic object key references with an implicit object fallback
  const price = isPremium 
    ? ((PRICING && PRICING[profile?.subscription_plan?.toUpperCase()]) || PRICING?.MONTHLY || 0) 
    : 0;

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h3 className="hidden lg:block text-lg font-bold text-brown-900 mb-2">Subscription Portal</h3>
        <p className="text-xs text-brown-600">Review our plans and pricing options</p>
      </div>
      <div className={`border-2 rounded-xl p-6 ${isPremium ? 'border-yellow-400 bg-gradient-to-br from-yellow-50/40 to-orange-50/20' : 'border-cream-300 bg-white/50'}`}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center space-x-2 mb-1.5">
              <h4 className="text-base font-bold text-brown-900">{isPremium ? 'Premium Tier Membership' : 'Standard Free Plan'}</h4>
              {isPremium && <Crown className="w-5 h-5 text-yellow-600 fill-yellow-500" />}
            </div>
            <p className="text-2xl font-black text-brown-900">
              {isPremium ? `₦${price.toLocaleString()}` : 'Free Access'}
              {isPremium && <span className="text-xs text-brown-600 font-normal">/{getDisplayPeriod(profile?.subscription_plan)}</span>}
            </p>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${isPremium ? 'bg-green-100 text-green-700' : 'bg-brown-100 text-brown-700'}`}>{isPremium ? 'Active' : 'Free Sandbox'}</span>
        </div>
        <div className="space-y-2 mb-5 border-t border-cream-200/60 pt-4 text-xs sm:text-sm text-brown-700">
          {isPremium ? (
            <>
              <div className="flex items-center space-x-2"><Check className="w-4 h-4 text-green-600" /><span>Unlimited document core uploads</span></div>
              <div className="flex items-center space-x-2"><Check className="w-4 h-4 text-green-600" /><span>High scalability parsing engine</span></div>
            </>
          ) : (
            <>
              <div className="flex items-center space-x-2"><Check className="w-4 h-4 text-brown-600" /><span>3 textbook uploads per active day</span></div>
              <div className="flex items-center space-x-2"><Check className="w-4 h-4 text-brown-600" /><span>Standard basic processing access</span></div>
            </>
          )}
        </div>
        {!isPremium ? (
          <button onClick={onUpgrade} className="w-full py-2.5 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-xl font-bold text-sm">Upgrade Membership Level</button>
        ) : (
          <button onClick={() => alert('Subscription operations run securely via native gateways')} className="w-full py-2.5 border-2 border-brown-300 text-brown-700 rounded-xl font-medium text-sm">Manage System Access</button>
        )}
      </div>
    </div>
  );
};

export default Settings;